import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
  Box,
} from "@mui/material";
import DialogContentText from "@mui/material/DialogContentText";
import makeStyles from "@mui/styles/makeStyles";
import Button from "@mui/material/Button";

import { notBlank } from "../util/ext";
import Grid from "@mui/material/Grid";
import { append, equals, includes, not, reject } from "ramda";
import Typography from "@mui/material/Typography";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import OrganizationAutoComplete from "../user-admin/OrganizationAutoComplete";
import { useBarcodePrefixUpdate } from "./authenticationService";

const useStyles = makeStyles(theme => ({
  errorContainer: {
    marginTop: theme.spacing(1),
    fontWeight: "bold",
    color: theme.palette.error.main,
  },

  title: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },

  prefixList: {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.primary.dark,
  },

  emptyList: {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.primary.dark,
    fontSize: "1.2rem",
    fontStyle: "italic",
    color: theme.palette.primary.main,
  },
}));

const AddBarcodeAuthenticationDialog = ({
  open,
  organizationId,
  authentication,
  onSave,
  onClose,
}) => {
  const classes = useStyles();
  const [errorMessage, setErrorMessage] = useState(null);
  const [prefix, setPrefix] = useState("");
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [prefixList, setPrefixList] = useState([]);
  const [initialAuth, setInitialAuth] = useState(null);

  useEffect(() => {
    if (open && notBlank(authentication)) {
      setSelectedOrganizationId(authentication.organizationId);
      setInitialAuth(authentication);
      setPrefixList(authentication.prefixes || []);
    } else {
      setSelectedOrganizationId(organizationId);
      setInitialAuth({});
      setPrefixList([]);
    }
  }, [open, authentication, organizationId]);

  const handlePrefixChange = useCallback(event => {
    setErrorMessage("");
    const value = event.target.value;
    setPrefix(value);
  }, []);

  const handlePrefixAdd = useCallback(() => {
    if (notBlank(prefix) && not(includes(prefix, prefixList))) {
      setPrefixList(prev => append(prefix, prev));
      setPrefix("");
      setErrorMessage("");
    } else {
      setErrorMessage("Invalid or exising prefix.");
    }
  }, [prefix, prefixList]);

  const handlePrefixRemove = useCallback(value => {
    setPrefixList(prev => reject(equals(value), prev));
  }, []);

  const handleOrganizationChange = orgId => {
    setSelectedOrganizationId(orgId);
  };

  const updateBarcodePrefixes = useBarcodePrefixUpdate();
  const handleApply = useCallback(async () => {
    if (notBlank(selectedOrganizationId)) {
      try {
        if (not(equals(initialAuth.prefixes, prefixList))) {
          await updateBarcodePrefixes.mutateAsync({
            resourceIds: {
              organizationId: selectedOrganizationId,
              type: "barcode",
            },
            data: prefixList,
          });
        }

        onSave(prefixList);
      } catch (error) {
        setErrorMessage(error);
      }
    } else {
      setErrorMessage("An Organization is Required.");
    }
  }, [
    selectedOrganizationId,
    initialAuth,
    prefixList,
    updateBarcodePrefixes,
    onSave,
  ]);

  const handleCancel = useCallback(() => onClose(), [onClose]);

  return (
    <Dialog open={open} aria-labelledby="barcode-dialog-title">
      <DialogTitle id="barcode-dialog-title">
        {notBlank(initialAuth)
          ? "Edit Barcode Authentication"
          : "Add New Barcode Authentication"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>Add Barcode Prefixes.</DialogContentText>
        {notBlank(errorMessage) && (
          <div className={classes.errorContainer}>{errorMessage}</div>
        )}
        <Box>
          <Grid alignItems="flex-end" container spacing={2}>
            <Grid item xs={12}>
              <OrganizationAutoComplete
                autoFocus
                disabled={notBlank(initialAuth)}
                value={selectedOrganizationId}
                label="Organization"
                onChange={handleOrganizationChange}
              />
            </Grid>
            <Grid item xs={5}>
              <Typography variant="body1" className={classes.title}>
                Barcode Prefixes
              </Typography>
              {notBlank(prefixList) ? (
                <List classes={{ root: classes.prefixList }}>
                  {prefixList.map(prefix => (
                    <ListItem key={prefix}>
                      <ListItemText primary={prefix} />
                      <ListItemSecondaryAction>
                        <IconButton
                          onClick={() => handlePrefixRemove(prefix)}
                          edge="end"
                          aria-label="delete"
                          size="large"
                        >
                          <CloseIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box className={classes.emptyList}>Add a Barcode Prefix...</Box>
              )}
            </Grid>
            <Grid item xs={5}>
              <TextField
                autoFocus
                id="prefix"
                label="Prefix"
                type="text"
                value={prefix}
                onChange={handlePrefixChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={2}>
              <div>
                <Button
                  onClick={handlePrefixAdd}
                  variant="text"
                  color="primary"
                >
                  Add
                </Button>
              </div>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBarcodeAuthenticationDialog;
